import { internalLogger } from '../../../interface/v1/clientOsLogger';
import { ValueStoreFacade } from '../../JWeb/facades';
import {
  ValueStoreGetResult,
  ValueStoreError,
  ValueStoreGetSuccessResult
} from '../../JWeb/types';
import type {
  ConsentResultType,
  ConsentStatusResult,
  ConsentValue
} from '../strategies/types';
import { generateOptanonConsentString } from './cookieHelpers';

/**
 * ValueStore consent key mappings.
 *
 * Based on:  https://github.azc.ext.hp.com/ClientOS/consents-northbound-api/blob/main/src/constants.ts
 */
const VALUE_STORE_CONSENT_KEYS = {
  BASIC_FUNCTIONALITY: ['@hpx/consents/app/basic'],
  ANALYTICS: ['@hpx/consents/app/productImprovement'],
  ADVERTISING: ['@hpx/consents/app/advertising']
} as const;

/**
 * Service for mapping ValueStore consents to Optanon cookie format.
 * Handles the translation from native consent storage to web consent format.
 
  The possible values for each key are "Opted In", "Opted Out" and "No Selection".

  Type	            Description
  dataTransfer	    This consent allows the app to transfer telemetry data.
  basic	            This consent covers basic functionalities of the app.
  analytics/productImprovement	This consent allows the app to collect telemetry data for product improvement purposes.
  advertising	        This consent allows the app to collect telemetry data for advertising purposes.


  Maps consent keys according to specifications:
    Basic Functionality → Group 1 (always 1:1)
    Analytics → Group 2 (2:1 for opt-in, 2:0 for opt-out)
    Advertising → Groups 3 & 4 (3:1,4:1 for opt-in, 3:0,4:0 for opt-out)

    The first character represents a consent group and the second represents whether the user accepted/responded or not. For example:

    1:1 = user responded or closed the consents banner
    1:0 = user didn't respond the consents banner yet
    2:1 = User accepted the "Performance and Analytics" consent
    2:0 = User didn't accept the "Performance and Analytics" consent
 */
export class ValueStoreConsentMapper {
  /**
   * Retrieves consent values from ValueStore for specified keys
   * @param keys Array of ValueStore keys to retrieve
   * @returns Promise resolving to consent values with their states
   */
  private static async getConsentValuesFromValueStore(
    keys: readonly string[]
  ): Promise<ConsentValue[]> {
    try {
      const result = await ValueStoreFacade.get({ keys: [...keys] });

      if (this.isValueStoreError(result)) {
        internalLogger?.error(
          'ValueStore consent retrieval error:',
          result.errorReason
        );
        // Return all keys as not found when there's an error
        return keys.map((key) => ({
          key,
          value: null,
          found: false
        }));
      }

      // Process successful result - ValueStore result structure has a 'result' property with an array
      const successResult =
        result.result as unknown as ValueStoreGetSuccessResult;

      return keys.map((key) => {
        // ValueStoreGetSuccessResult is an array of entries
        const entry = Array.isArray(successResult)
          ? successResult.find((entry) => entry.key === key)
          : undefined;

        return {
          key,
          // `entry` may be undefined when ValueStore doesn't return the key at all,
          // and `entry.value` may be `null` when the key exists without a stored value.
          // Neither case means the user explicitly opted out.
          value: entry ? entry.value : undefined,
          found: !!entry
        };
      });
    } catch (error) {
      internalLogger?.error(
        'Error retrieving consent values from ValueStore:',
        error
      );
      return keys.map((key) => ({
        key,
        value: null,
        found: false
      }));
    }
  }

  /**
   * Type guard to check if ValueStore result is an error
   * TODO: move this to valueStoreFacade as a helper method
   */
  private static isValueStoreError(
    result: ValueStoreGetResult | ValueStoreError
  ): result is ValueStoreError {
    return 'errorType' in result;
  }

  /**
   * Determines whether a ValueStore consent value represents an explicit
   * decision (as opposed to a missing/null value).
   */
  private static isExplicitConsentValue(
    value: ConsentValue['value']
  ): value is string {
    return typeof value === 'string';
  }

  /**
   * Determines consent status for a group based on ValueStore keys
   * @param keys Array of ValueStore keys to check
   * @returns Promise resolving to true if any key is consented to
   */
  public static async hasConsentForKeys(
    keys: readonly string[]
  ): Promise<boolean> {
    const { isOptedIn } = await this.getConsentStatusForKeys(keys);
    return isOptedIn;
  }

  /**
   * Determines consent status and completeness for a group of ValueStore keys.
   * A category is considered incomplete when none of its keys returned an
   * explicit decision (i.e. every key was missing or null), which should NOT
   * be treated as the user having opted out.
   * @param keys Array of ValueStore keys to check
   */
  public static async getConsentStatusForKeys(
    keys: readonly string[]
  ): Promise<ConsentStatusResult> {
    const consentValues = await this.getConsentValuesFromValueStore(keys);

    const isOptedIn = consentValues.some(
      (consent) => consent.found && consent.value === 'OptedIn'
    );
    /*
   // Not being used
    const isOptedOut = consentValues.some(
      (consent) => consent.found && consent.value === 'OptedOut'
    );
    */

    const isIncomplete = consentValues.every(
      (consent) => !consent.found || !this.isExplicitConsentValue(consent.value)
    );

    return { isOptedIn, isIncomplete };
  }

  /**
   * Lookup consent from ValueStore using direct key mapping
   */
  public static async lookupConsentFromValueStore(): Promise<ConsentResultType> {
    try {
      internalLogger?.debug?.(
        'NativeConsentStrategy: Looking up consents from ValueStore: ',
        VALUE_STORE_CONSENT_KEYS.BASIC_FUNCTIONALITY,
        VALUE_STORE_CONSENT_KEYS.ANALYTICS
      );

      // 1. Basic Functionality: Check if any basic functionality keys are true
      const basicFunctionalityStatus =
        await ValueStoreConsentMapper.getConsentStatusForKeys(
          VALUE_STORE_CONSENT_KEYS.BASIC_FUNCTIONALITY
        );

      // 2. Analytics: Check if any analytics keys are true
      const analyticsStatus =
        await ValueStoreConsentMapper.getConsentStatusForKeys(
          VALUE_STORE_CONSENT_KEYS.ANALYTICS
        );

      // 3. Advertising: Check if any advertising keys are true
      // For now, wont be used.
      /*const advertisingConsent =
        await ValueStoreConsentMapper.hasConsentForKeys(
          VALUE_STORE_CONSENT_KEYS.ADVERTISING
        );
        */
      const advertisingConsent = false;

      // Create Optanon groups based on ValueStore results
      const groupsObject: Record<string, 0 | 1> = {
        '1': basicFunctionalityStatus.isOptedIn ? 1 : 0, // Basic Functionality
        '2': analyticsStatus.isOptedIn ? 1 : 0, // Analytics
        '3': advertisingConsent ? 1 : 0, // Functional/Advertising
        '4': advertisingConsent ? 1 : 0 // Targeting/Advertising
      };

      // A key category is only "incomplete" when ValueStore gave no explicit
      // decision at all for it (missing or null keys). This must not be
      // treated as the user opting out - the caller should fall back to the
      // Consents API to get an authoritative answer.
      const isIncomplete =
        basicFunctionalityStatus.isIncomplete || analyticsStatus.isIncomplete;

      const optanonConsent = generateOptanonConsentString({
        groups: groupsObject
      });

      internalLogger?.debug?.(
        'NativeConsentStrategy: Mapped ValueStore consents to Optanon format: ',
        optanonConsent,
        'and isIncomplete: ',
        isIncomplete
      );

      return {
        optanonConsent,
        hasError: false,
        isIncomplete
      };
    } catch (error) {
      internalLogger?.error(
        'NativeConsentStrategy: ValueStore lookup failed:',
        error
      );
      return {
        optanonConsent: '',
        hasError: true,
        errorMessage: `ValueStore lookup failed: ${error}`
      };
    }
  }
}
